import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

interface FormFieldProps {
	name: string;
	label?: string;
	error?: string;
	warning?: string;
	required?: boolean;
	help?: string;
	/**
	 * The form control element. Typically a single input-like component
	 * (Input, Select, SingleSelect, ClientSelector, etc.) that FormField
	 * clones to inject `id` / `aria-*` / `size` props.
	 *
	 * Multiple children are tolerated: the first valid React element is
	 * treated as the form control and gets the cloned props; any further
	 * elements render as siblings after it (useful for inline hints /
	 * callouts that live tightly under the input). Booleans, nulls, and
	 * strings between the input and any trailing siblings are passed
	 * through unchanged.
	 */
	children: React.ReactNode;
	className?: string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
}

export function FormField({
	name,
	label,
	error,
	warning,
	required,
	help,
	children,
	className,
	size = 'md',
}: FormFieldProps) {
	const colors = useThemeColors();
	const fieldId = `field-${name}`;
	const errorId = `${fieldId}-error`;
	const warningId = `${fieldId}-warning`;
	const helpId = `${fieldId}-help`;

	// Determine label text size based on size prop
	const labelSizeClass = {
		xs: 'text-xs',
		sm: 'text-sm',
		md: 'text-sm',
		lg: 'text-base',
	}[size];

	// Locate the first valid React element to treat as the form control —
	// the rest render as siblings. Without this normalization, callers that
	// pass `<Input/>` + a conditional inline hint (e.g. DiscrepancyHint on
	// the license edit panel) hand us a children *array*, and
	// `React.cloneElement(array, …)` returns `{type: undefined, …}` which
	// blows up at render with "Element type is invalid: got: undefined".
	const childArray = React.Children.toArray(children);
	const firstElementIdx = childArray.findIndex(React.isValidElement);
	const formControl =
		firstElementIdx >= 0
			? (childArray[firstElementIdx] as React.ReactElement)
			: null;
	const trailingChildren =
		firstElementIdx >= 0 ? childArray.slice(firstElementIdx + 1) : [];

	const childWithProps =
		formControl != null
			? React.cloneElement(formControl, {
					id: fieldId,
					'aria-invalid': !!error,
					'aria-describedby':
						[error && errorId, warning && warningId, help && helpId]
							.filter(Boolean)
							.join(' ') || undefined,
					'aria-required': required,
					size: size,
				})
			: null;

	return (
		<div className={cn('space-y-1', className)}>
			{label && (
				<label
					htmlFor={fieldId}
					className={cn(
						'block font-medium',
						labelSizeClass,
						colors.classes.textPrimary,
					)}
				>
					{label}
					{required === true && (
						<span
							className={cn(colors.classes.textDanger, 'ml-1')}
							aria-label="required"
						>
							*
						</span>
					)}
				</label>
			)}

			{childWithProps}

			{trailingChildren}

			{error && (
				<p
					id={errorId}
					className={cn(labelSizeClass, colors.classes.textDanger)}
					role="alert"
				>
					{error}
				</p>
			)}

			{warning && !error && (
				<p
					id={warningId}
					className={cn(labelSizeClass, colors.classes.textWarning)}
				>
					{warning}
				</p>
			)}

			{help && !error && !warning && (
				<p
					id={helpId}
					className={cn(labelSizeClass, colors.classes.textSecondary)}
				>
					{help}
				</p>
			)}
		</div>
	);
}
